import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import type { IncidentItem, Stats } from '../../src/api/queries';
import type { TrafficFacets, TrafficStats } from '../../src/api/traffic-queries';
import type { Source } from '../../src/source';
import {
  fetchIncidents,
  fetchSeries,
  fetchStats,
  fetchTrafficFacets,
  fetchTrafficStats,
  incidentsUrl,
  seriesUrl,
  statsUrl,
  type TrafficWindow,
  trafficFacetsUrl,
  trafficStatsUrl,
} from './api';
import type { SeriesMetricKey } from './coverage';
import { daysBefore, todayInTallinn } from './format';
import { PLATE_CATEGORIES } from './labels';
import { type CounterInput, counterRate } from './useLiveCounter';
import { viewFilters } from './useStats';
import { DEFAULT_VIEW_STATE, type ViewState } from './useUrlState';

/**
 * The landing page's data layer: the year-to-date window every hero figure is counted over, and
 * the two requests each counter needs — the baseline from `/api/stats`, the pace from a weekly
 * `/api/series`. No filters reach this page, so the window is the only thing that varies, and it
 * changes once a day, which keeps these URLs (and the query cache behind them) stable.
 */

/** The counters' window: this calendar year so far, in Tallinn's calendar rather than UTC's. */
export function ytdWindow(now: () => number = Date.now): { from: string; to: string } {
  const to = todayInTallinn(now);
  return { from: `${to.slice(0, 4)}-01-01`, to };
}

/** The year-to-date view state the landing's queries share; weekly, because the pace is weekly. */
function ytdState(): ViewState {
  return { ...DEFAULT_VIEW_STATE, ...ytdWindow(), g: 'week' };
}

export interface CounterFeed {
  input: Omit<CounterInput, 'coverage'>;
  isPending: boolean;
}

/** The rate's own window, not the baseline's: a year-to-date window opens on a bucket whose Monday
 * sits in December, and in the first days of January none has closed at all. */
const RATE_WINDOW_DAYS = 97;

export function rateWindow(asOf: string): { from: string; to: string } {
  return { from: daysBefore(asOf, RATE_WINDOW_DAYS), to: asOf };
}

/** A counter's pace. Disabled until `as_of` is known, so an idle query is not reported as loading. */
function useCounterRate(
  asOf: string | null,
  source: Source,
  category: string,
  metric: SeriesMetricKey,
): { perDay: number | null; isPending: boolean } {
  const window = asOf === null ? { from: '2000-01-01', to: '2000-01-01' } : rateWindow(asOf);
  const filters = viewFilters(category, { ...DEFAULT_VIEW_STATE, ...window, g: 'week' }, source);
  const series = useQuery({
    queryKey: [seriesUrl(filters)],
    queryFn: () => fetchSeries(filters),
    enabled: asOf !== null,
  });
  return {
    perDay:
      asOf === null || series.data === undefined
        ? null
        : counterRate(series.data, metric, asOf, window.from),
    isPending: asOf !== null && series.isPending,
  };
}

/** One digest counter's baseline and pace. `metric` is the series column the pace is measured on —
 * `damage_cents` for a euro counter, `incident_count` for a tally. */
export function useDigestCounter(
  category: string,
  metric: SeriesMetricKey,
): CounterFeed & { stats: Stats | undefined } {
  const state = ytdState();
  const filters = viewFilters(category, state, 'digest');

  const stats = useQuery({ queryKey: [statsUrl(filters)], queryFn: () => fetchStats(filters) });
  const asOf = stats.data?.last_date ?? null;
  const rate = useCounterRate(asOf, 'digest', category, metric);

  const value =
    stats.data === undefined
      ? 0
      : metric === 'damage_cents'
        ? stats.data.damage_cents_total
        : stats.data.incident_count;

  return {
    stats: stats.data,
    isPending: stats.isPending || rate.isPending,
    input: {
      // Both halves, because a tile with the baseline but no pace yet would print "the recent pace
      // cannot be estimated" — a claim about the corpus, made about a request still in flight.
      pending: stats.isPending || rate.isPending,
      baseline: { value, asOf },
      perDay: rate.perDay,
      today: state.to,
    },
  };
}

/** The traffic counter, off its own two endpoints — the series is in the path, never a parameter. */
export function useTrafficCounter(): CounterFeed & { stats: TrafficStats | undefined } {
  const state = ytdState();
  const window: TrafficWindow = { from: state.from, to: state.to };

  const stats = useQuery({
    queryKey: [trafficStatsUrl(window)],
    queryFn: () => fetchTrafficStats(window),
  });
  const asOf = stats.data?.last_date ?? null;
  const rate = useCounterRate(asOf, 'traffic', 'ALL', 'incident_count');

  return {
    stats: stats.data,
    isPending: stats.isPending || rate.isPending,
    input: {
      pending: stats.isPending || rate.isPending,
      baseline: { value: stats.data?.accidents ?? 0, asOf },
      perDay: rate.perDay,
      today: state.to,
    },
  };
}

/** The leaderboard's window: the last 30 days, ending today. A rolling month is the shortest span
 * that puts a readable number in every county; it moves once a day, like every other URL here. */
export function last30Days(now: () => number = Date.now): { from: string; to: string } {
  const to = todayInTallinn(now);
  return { from: daysBefore(to, 29), to };
}

/** The record plate's window: this calendar month so far — "kuu suurim" means this month's. */
export function monthToDate(now: () => number = Date.now): { from: string; to: string } {
  const to = todayInTallinn(now);
  return { from: `${to.slice(0, 7)}-01`, to };
}

/** One `/api/traffic/facets` answer for a named window; the Tabloo asks for two different ones. */
export function useTrafficFacetsFor(window: TrafficWindow): UseQueryResult<TrafficFacets, Error> {
  return useQuery({
    queryKey: [trafficFacetsUrl(window)],
    queryFn: () => fetchTrafficFacets(window),
  });
}

/** The biggest stated damage a plate may show. Doubted sums are excluded server-side; the
 * allow-list here is what keeps a death or a domestic-violence report off a property superlative. */
const PLATE_SCAN = 25;

export function useBiggestDamage(window: {
  from: string;
  to: string;
}): UseQueryResult<IncidentItem | null, Error> {
  const filters = {
    ...viewFilters('ALL', { ...DEFAULT_VIEW_STATE, ...window }, 'digest'),
    limit: PLATE_SCAN,
    order: 'damage_desc',
  };
  return useQuery({
    queryKey: [incidentsUrl(filters)],
    queryFn: async () => {
      const { items } = await fetchIncidents(filters);
      return items.find((i) => PLATE_CATEGORIES.includes(i.category)) ?? null;
    },
  });
}
